import { useState, useEffect, useRef } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import PageTransition from '../../components/base/PageTransition';
import SEOHead from '../../components/base/SEOHead';
import SkillsDownloadsContent from './components/SkillsDownloadsContent';
import SkillsInstallContent from './components/SkillsInstallContent';

type SectionId = 'install' | 'downloads';

interface NavItem {
  id: SectionId;
  label: string;
  icon: string;
}

const navItems: NavItem[] = [
  { id: 'install', label: '安装', icon: 'ri-install-line' },
  { id: 'downloads', label: '技能下载', icon: 'ri-download-cloud-line' },
];

const contentMap: Record<SectionId, React.ReactNode> = {
  install: <SkillsInstallContent />,
  downloads: <SkillsDownloadsContent />,
};

export default function SkillsPage() {
  const [activeSection, setActiveSection] = useState<SectionId>(() => {
    const saved = localStorage.getItem('skills_active_section');
    return (saved as SectionId) || 'install';
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://openclaw.cc';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: 'Skills 技能系统',
    description: 'OpenClaw 技能安装与下载指南 - 扩展智能体能力的插件系统',
    url: `${siteUrl}/skills`,
    datePublished: '2024-01-01',
    dateModified: new Date().toISOString().split('T')[0],
    author: {
      '@type': 'Organization',
      name: 'OpenClaw',
    },
    publisher: {
      '@type': 'Organization',
      name: 'OpenClaw',
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/lobster.svg`,
      },
    },
    inLanguage: 'zh-CN',
  };

  // 监听导航栏子菜单点击
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'skills_active_section' && e.newValue) {
        setActiveSection(e.newValue as SectionId);
      }
    };

    const handleNavChange = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.storageKey === 'skills_active_section') {
        setActiveSection(detail.key as SectionId);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('nav-section-change', handleNavChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('nav-section-change', handleNavChange);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('skills_active_section', activeSection);
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [activeSection]);

  const handleSectionChange = (e: React.MouseEvent, id: SectionId) => {
    e.preventDefault();
    if (id === activeSection) return;
    setActiveSection(id);
    setMobileMenuOpen(false);
  };

  const currentNavItem = navItems.find(item => item.id === activeSection);

  return (
    <PageTransition>
      <SEOHead
        title="Skills 技能系统 | OpenClaw 技能安装与下载"
        description="OpenClaw 技能安装与下载指南 - 扩展智能体能力的插件系统，支持自定义技能开发"
        keywords="OpenClaw技能, Skills系统, 智能体插件, 技能安装, 技能开发"
        canonical={`${siteUrl}/skills`}
        jsonLd={jsonLd}
      />
      <div className="min-h-screen bg-slate-950 flex flex-col">
        <Navbar />

        {/* Mobile Section Selector */}
        <div className="md:hidden sticky top-16 z-40 bg-slate-900 border-b border-slate-800">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="w-full flex items-center justify-between px-4 py-3 text-left cursor-pointer"
          >
            <div className="flex items-center gap-2.5">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${currentNavItem?.icon} text-cyan-400`}></i>
              </div>
              <span className="text-white font-medium text-sm">{currentNavItem?.label}</span>
            </div>
            <i className={`ri-arrow-down-s-line text-slate-400 transition-transform duration-200 ${mobileMenuOpen ? 'rotate-180' : ''}`}></i>
          </button>

          {/* Mobile Dropdown Menu */}
          {mobileMenuOpen && (
            <div className="absolute top-full left-0 right-0 bg-slate-900 border-b border-slate-800 max-h-[70vh] overflow-y-auto">
              <nav className="p-3">
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={(e) => handleSectionChange(e, item.id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap mb-0.5 text-left ${
                      activeSection === item.id
                        ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20'
                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
                    }`}
                  >
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-base`}></i>
                    </div>
                    {item.label}
                  </button>
                ))}
              </nav>
            </div>
          )}
        </div>

        <div className="flex flex-1 pt-16" style={{ height: 'calc(100vh - 0px)' }}>
          {/* Main Content */}
          <main
            ref={contentRef}
            className="flex-1 overflow-y-auto"
            style={{ height: 'calc(100vh - 64px)' }}
          >
            <div className="max-w-6xl mx-auto px-10 md:px-20 py-14 min-h-full">
              {contentMap[activeSection]}
            </div>
            <Footer />
          </main>
        </div>
      </div>
    </PageTransition>
  );
}